import {
  createDefaultAppearance,
  normalizeEditorConfig,
  normalizeSceneDocument,
  renderMaterialDefinitions,
  serializeRenderState,
  type RenderEditorConfig,
  type RenderMaterialDefinition,
  type RenderMeshGroup,
  type RenderSceneDocument,
} from "@/lib/render-core/scene-contract";

export const PRODUCT_RENDER_OPTIONS_EVENT = "product-render:options";
const PRODUCT_RENDER_SELECTION_STORAGE_PREFIX = "insparkle:product-render-selection:";

export type ProductRenderSelection = {
  goodsId: string;
  materialId?: string;
  materialLabel?: string;
  materialRenderCode?: string;
  craftId?: string;
  craftLabel?: string;
  craftRenderCode?: string;
};

export type ProductRenderPayload = {
  upload?: {
    id?: string;
    originalFileName?: string;
    fileSizeBytes?: number;
  };
  record?: {
    id?: string;
    legacyGoodsId?: string;
  };
  snapshot?: {
    sceneJson?: string | null;
    configJson?: string | null;
  };
};

export type ProductRenderResourceMaterial = {
  id: string;
  legacyPropertyValueId?: number | null;
  legacyRenderCode?: string | null;
  label: string;
  labelEn?: string | null;
  prefix: string;
  color: string;
  thumbnailColor: string;
  metalness: number;
  roughness: number;
  clearcoat: number;
  envMapIntensity: number;
  reflectivity: number;
  thumbnailUrl?: string | null;
  baseColorMapUrl?: string | null;
  normalMapUrl?: string | null;
  aoMapUrl?: string | null;
  roughnessMapUrl?: string | null;
  metalnessMapUrl?: string | null;
  sourceLabel: string;
  status: string;
  notes?: string | null;
};

export type ProductRenderResourceMapping = {
  id: string;
  legacyGoodsId?: string | null;
  legacyModelId?: number | null;
  legacyMaterialPropertyValueId?: number | null;
  legacyCraftPropertyValueId?: number | null;
  legacyMaterialRenderCode?: string | null;
  legacyCraftRenderCode?: string | null;
  material: ProductRenderResourceMaterial;
  sourceLabel: string;
  status: string;
  notes?: string | null;
};

export type ProductRenderResourcePayload = {
  mode: string;
  reason?: string | null;
  legacyGoodsId: string;
  mappings: ProductRenderResourceMapping[];
};

export type ProductRenderDraft = {
  recordId: string;
  uploadId?: string;
  fileName: string;
  scene: RenderSceneDocument;
  config: RenderEditorConfig;
};

export type ProductRenderCartSnapshot = {
  recordId: string;
  sceneJson: string;
  configJson: string;
  priceSnapshotJson: string;
  notes: string;
};

export type ProductRenderCartSnapshotContext = {
  materialId?: string;
  materialLabel?: string;
  craftId?: string;
  craftLabel?: string;
  quantity: number;
  unitPrice?: number;
  totalPrice?: number;
};

const currentProductRenderDrafts = new Map<string, ProductRenderDraft>();

function defaultGroupId(kind: RenderMeshGroup["kind"]) {
  if (kind === "gem") return "gemstone";
  if (kind === "unassigned") return "unassigned";
  return "metal";
}

function normalizeToken(value?: string) {
  return (value || "").trim().toLowerCase();
}

function localMaterialDefinition(
  id: string,
  label: string,
  color: string,
  options: Partial<Pick<RenderMaterialDefinition, "metalness" | "roughness" | "clearcoat" | "envMapIntensity" | "reflectivity">> = {},
): RenderMaterialDefinition {
  return {
    id,
    label,
    prefix: id,
    color,
    thumbnailColor: color,
    metalness: options.metalness ?? 1,
    roughness: options.roughness ?? 0.08,
    clearcoat: options.clearcoat ?? 0.62,
    envMapIntensity: options.envMapIntensity ?? 1.18,
    reflectivity: options.reflectivity ?? 1,
    sourceLabel: "insparkle-new:product-detail-legacy-option-map",
    status: "fallback",
    notes: "Product-detail-only fallback for legacy commerce options that do not have a mature render material record yet.",
  };
}

const productMaterialDefinitions: RenderMaterialDefinition[] = [
  localMaterialDefinition("metal-925-silver", "925 silver", "#d7d9d8", {
    metalness: 1,
    roughness: 0.06,
    envMapIntensity: 1.08,
    reflectivity: 0.92,
  }),
  localMaterialDefinition("metal-925-silver-brushed", "925 silver brushed", "#c8cbca", {
    metalness: 1,
    roughness: 0.18,
    envMapIntensity: 0.95,
    reflectivity: 0.82,
  }),
  localMaterialDefinition("metal-925-silver-matte", "925 silver matte", "#bfc2c1", {
    metalness: 0.92,
    roughness: 0.42,
    clearcoat: 0.24,
    envMapIntensity: 0.82,
    reflectivity: 0.72,
  }),
  localMaterialDefinition("metal-brass", "brass", "#c39835", {
    metalness: 0.96,
    roughness: 0.12,
    envMapIntensity: 1.02,
    reflectivity: 0.88,
  }),
  localMaterialDefinition("metal-brass-brushed", "brass brushed", "#b28930", {
    metalness: 0.96,
    roughness: 0.24,
    envMapIntensity: 0.9,
    reflectivity: 0.78,
  }),
  localMaterialDefinition("metal-brass-matte", "brass matte", "#a77f2d", {
    metalness: 0.9,
    roughness: 0.5,
    clearcoat: 0.18,
    envMapIntensity: 0.78,
    reflectivity: 0.66,
  }),
  localMaterialDefinition("metal-plated-black", "black plating", "#060606", {
    metalness: 1,
    roughness: 0.2,
    clearcoat: 0.34,
    envMapIntensity: 0.58,
    reflectivity: 0.42,
  }),
];

function allProductRenderMaterials() {
  return [...renderMaterialDefinitions, ...productMaterialDefinitions];
}

function dbResourceMaterialDefinition(resource: ProductRenderResourceMaterial): RenderMaterialDefinition {
  return {
    id: resource.id,
    label: resource.labelEn || resource.label,
    prefix: resource.prefix,
    color: resource.color,
    thumbnailColor: resource.thumbnailColor || resource.color,
    metalness: Number(resource.metalness),
    roughness: Number(resource.roughness),
    clearcoat: Number(resource.clearcoat),
    envMapIntensity: Number(resource.envMapIntensity),
    reflectivity: Number(resource.reflectivity),
    thumbnailUrl: resource.thumbnailUrl ?? undefined,
    baseColorMapUrl: resource.baseColorMapUrl ?? undefined,
    normalMapUrl: resource.normalMapUrl ?? undefined,
    aoMapUrl: resource.aoMapUrl ?? undefined,
    roughnessMapUrl: resource.roughnessMapUrl ?? undefined,
    metalnessMapUrl: resource.metalnessMapUrl ?? undefined,
    sourceLabel: resource.sourceLabel || "database",
    status: "external",
    notes: resource.notes || "Database-backed product render material.",
  };
}

function materialMatchesToken(material: RenderMaterialDefinition, token: string) {
  return (
    normalizeToken(material.id) === token ||
    normalizeToken(material.prefix) === token ||
    normalizeToken(material.label) === token
  );
}

function findMaterialByToken(value?: string) {
  const token = normalizeToken(value);
  if (!token) return undefined;
  return allProductRenderMaterials().find((material) => materialMatchesToken(material, token));
}

function findMaterialById(id: string) {
  return allProductRenderMaterials().find((material) => material.id === id);
}

type ProductMaterialFamily = "yellow" | "red" | "white" | "silver" | "brass" | "black";
type ProductMaterialFinish = "polished" | "brushed" | "matte";

function resolveMaterialFamily(selection: ProductRenderSelection): ProductMaterialFamily | undefined {
  const materialId = normalizeToken(selection.materialId);
  if (materialId === "1") return "yellow";
  if (materialId === "4") return "red";
  if (materialId === "5") return "white";
  if (materialId === "6") return "silver";
  if (materialId === "15") return "brass";

  const text = normalizeToken([selection.materialLabel, selection.materialRenderCode].join(" "));
  if (/\u9ec4\u94dc|brass|copper/.test(text)) return "brass";
  if (/925|\u94f6|silver/.test(text)) return "silver";
  if (/\u73ab\u7470|\u7ea2|rose|red/.test(text)) return "red";
  if (/\u767d|white|platinum/.test(text)) return "white";
  if (/\u9ec4|\u9ec4\u91d1|yellow|gold/.test(text)) return "yellow";
  return undefined;
}

function resolveCraftOverride(selection: ProductRenderSelection): {
  family?: ProductMaterialFamily;
  finish?: ProductMaterialFinish;
} {
  const craftId = normalizeToken(selection.craftId);
  const craftCode = normalizeToken(selection.craftRenderCode);
  const craftText = normalizeToken([selection.craftLabel, selection.craftRenderCode].join(" "));
  const result: { family?: ProductMaterialFamily; finish?: ProductMaterialFinish } = {};

  if (craftId === "10" || craftCode === "10013937" || /\u7535\u9540.*\u9ec4/.test(craftText)) {
    result.family = "yellow";
  } else if (craftId === "11" || craftCode === "10011877" || /\u7535\u9540.*\u7ea2/.test(craftText)) {
    result.family = "red";
  } else if (craftId === "12" || craftCode === "10011878" || /\u7535\u9540.*\u767d/.test(craftText)) {
    result.family = "white";
  } else if (craftId === "14" || craftCode === "100112740" || /\u9ed1|black/.test(craftText)) {
    result.family = "black";
  }

  if (craftId === "3" || craftCode === "10011859" || /\u62c9\u4e1d|brushed|drawing/.test(craftText)) {
    result.finish = "brushed";
  } else if (craftId === "7" || craftCode === "10011874" || /\u55b7\u7802|\u7802|\u78e8|matte|sand/.test(craftText)) {
    result.finish = "matte";
  } else if (craftId === "2" || craftCode === "10011850" || /\u629b\u5149|polish/.test(craftText)) {
    result.finish = "polished";
  }

  return result;
}

function productMaterialId(family: ProductMaterialFamily, finish: ProductMaterialFinish) {
  if (family === "black") return "metal-plated-black";
  const finishSuffix = finish === "polished" ? "" : `-${finish}`;
  if (family === "silver") return `metal-925-silver${finishSuffix}`;
  if (family === "brass") return `metal-brass${finishSuffix}`;
  return `metal-18k-${family}${finishSuffix}`;
}

function resolveProductMaterial(selection: ProductRenderSelection) {
  const directMatch = findMaterialByToken(selection.materialRenderCode) ?? findMaterialByToken(selection.materialId);
  const craftOverride = resolveCraftOverride(selection);
  const family = craftOverride.family ?? resolveMaterialFamily(selection);
  const finish = craftOverride.finish ?? "polished";

  if (family) {
    return findMaterialById(productMaterialId(family, finish)) ?? findMaterialById(productMaterialId(family, "polished")) ?? directMatch;
  }

  if (directMatch) {
    if (finish !== "polished" && directMatch.id.startsWith("metal-18k-")) {
      const baseId = directMatch.id.replace(/-(matte|brushed)$/, "");
      return findMaterialById(`${baseId}-${finish}`) ?? directMatch;
    }
    return directMatch;
  }

  return undefined;
}

function matchesLegacyValue(selectionValue?: string, mappedValue?: number | string | null) {
  return Boolean(selectionValue && mappedValue != null && normalizeToken(selectionValue) === normalizeToken(String(mappedValue)));
}

function resolveProductResourceMaterial(selection: ProductRenderSelection, resources?: ProductRenderResourcePayload | null) {
  if (!resources?.mappings?.length) return undefined;

  const scored = resources.mappings
    .map((mapping) => {
      const materialMatch =
        matchesLegacyValue(selection.materialId, mapping.legacyMaterialPropertyValueId) ||
        matchesLegacyValue(selection.materialRenderCode, mapping.legacyMaterialRenderCode) ||
        matchesLegacyValue(selection.materialRenderCode, mapping.material.legacyRenderCode) ||
        matchesLegacyValue(selection.materialId, mapping.material.legacyPropertyValueId);
      const craftMatch =
        matchesLegacyValue(selection.craftId, mapping.legacyCraftPropertyValueId) ||
        matchesLegacyValue(selection.craftRenderCode, mapping.legacyCraftRenderCode);
      const materialWildcard = mapping.legacyMaterialPropertyValueId == null && !mapping.legacyMaterialRenderCode;
      const craftWildcard = mapping.legacyCraftPropertyValueId == null && !mapping.legacyCraftRenderCode;

      let score = 0;
      if (materialMatch) score += 10;
      if (craftMatch) score += 6;
      if (materialWildcard) score += 1;
      if (craftWildcard) score += 1;
      return { mapping, score, materialMatch, craftMatch, materialWildcard, craftWildcard };
    })
    .filter((item) => (item.materialMatch || item.materialWildcard) && (item.craftMatch || item.craftWildcard))
    .sort((left, right) => right.score - left.score);

  return scored[0]?.mapping.material ? dbResourceMaterialDefinition(scored[0].mapping.material) : undefined;
}

export function rememberProductRenderSelection(selection: ProductRenderSelection) {
  if (typeof window === "undefined" || !selection.goodsId) {
    return;
  }

  try {
    window.sessionStorage.setItem(`${PRODUCT_RENDER_SELECTION_STORAGE_PREFIX}${selection.goodsId}`, JSON.stringify(selection));
  } catch {
    // Session storage can be unavailable in privacy-restricted browser contexts.
  }
}

export function rememberCurrentProductRenderDraft(goodsId: string, draft: ProductRenderDraft | null) {
  if (!goodsId) return;
  if (draft) {
    currentProductRenderDrafts.set(goodsId, draft);
    return;
  }

  currentProductRenderDrafts.delete(goodsId);
}

export function createProductRenderCartSnapshot(
  goodsId: string,
  context: ProductRenderCartSnapshotContext,
): ProductRenderCartSnapshot | null {
  const draft = currentProductRenderDrafts.get(goodsId);
  if (!draft) {
    return null;
  }

  const serialized = serializeRenderState(draft.scene, draft.config);
  const resolvedMaterials = Object.entries(draft.config.groups)
    .filter(([, appearance]) => appearance.kind === "metal")
    .map(([groupId, appearance]) => ({
      groupId,
      materialId: appearance.materialId,
      color: appearance.color,
      metalness: appearance.metalness,
      roughness: appearance.roughness,
      clearcoat: appearance.clearcoat,
      envMapIntensity: appearance.envMapIntensity,
      reflectivity: appearance.reflectivity,
      baseColorMapUrl: appearance.baseColorMapUrl,
      normalMapUrl: appearance.normalMapUrl,
      aoMapUrl: appearance.aoMapUrl,
      roughnessMapUrl: appearance.roughnessMapUrl,
      metalnessMapUrl: appearance.metalnessMapUrl,
    }));

  return {
    recordId: draft.recordId,
    sceneJson: serialized.sceneJson,
    configJson: serialized.configJson,
    priceSnapshotJson: JSON.stringify({
      goodsId,
      materialId: context.materialId,
      materialLabel: context.materialLabel,
      craftId: context.craftId,
      craftLabel: context.craftLabel,
      quantity: context.quantity,
      unitPrice: context.unitPrice,
      totalPrice: context.totalPrice,
      resolvedMaterials,
    }),
    notes: "Saved from product detail add-to-cart",
  };
}

function readRememberedProductRenderSelection(goodsId: string): ProductRenderSelection | null {
  if (typeof window === "undefined" || !goodsId) {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(`${PRODUCT_RENDER_SELECTION_STORAGE_PREFIX}${goodsId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ProductRenderSelection;
    return parsed?.goodsId === goodsId ? parsed : null;
  } catch {
    return null;
  }
}

export function applyRememberedProductRenderSelection(draft: ProductRenderDraft, goodsId: string, resources?: ProductRenderResourcePayload | null): ProductRenderDraft;
export function applyRememberedProductRenderSelection(draft: ProductRenderDraft | null, goodsId: string, resources?: ProductRenderResourcePayload | null): ProductRenderDraft | null;
export function applyRememberedProductRenderSelection(draft: ProductRenderDraft | null, goodsId: string, resources?: ProductRenderResourcePayload | null): ProductRenderDraft | null {
  if (!draft) {
    return draft;
  }

  const remembered = readRememberedProductRenderSelection(goodsId);
  return remembered ? applyProductRenderSelection(draft, remembered, resources) : draft;
}

export function applyProductRenderSelection(draft: ProductRenderDraft, selection: ProductRenderSelection, resources?: ProductRenderResourcePayload | null): ProductRenderDraft {
  const material = resolveProductResourceMaterial(selection, resources) ?? resolveProductMaterial(selection);
  if (!material) {
    return draft;
  }

  const metalGroupIds = draft.scene.meshGroups.filter((group) => group.kind === "metal").map((group) => group.id);
  if (!metalGroupIds.length && draft.config.groups.metal) {
    metalGroupIds.push("metal");
  }
  if (!metalGroupIds.length) {
    return draft;
  }

  const groups = { ...draft.config.groups };
  for (const groupId of metalGroupIds) {
    const current = groups[groupId] ?? createDefaultAppearance("metal");
    groups[groupId] = {
      ...current,
      color: material.color,
      materialId: material.id,
      baseColorMapUrl: material.baseColorMapUrl,
      normalMapUrl: material.normalMapUrl,
      aoMapUrl: material.aoMapUrl,
      roughnessMapUrl: material.roughnessMapUrl,
      metalnessMapUrl: material.metalnessMapUrl,
      metalness: material.metalness,
      roughness: material.roughness,
      clearcoat: material.clearcoat,
      envMapIntensity: material.envMapIntensity,
      reflectivity: material.reflectivity,
    };
  }

  return {
    ...draft,
    config: {
      ...draft.config,
      groups,
    },
  };
}

export function applyDiscoveredProductGroups(draft: ProductRenderDraft, groups: RenderMeshGroup[]): ProductRenderDraft {
  if (draft.scene.meshGroups.length) {
    return draft;
  }

  const configGroups = { ...draft.config.groups };
  for (const group of groups) {
    configGroups[group.id] = createDefaultAppearance(group.kind, configGroups[group.id] ?? configGroups[defaultGroupId(group.kind)]);
  }

  return {
    ...draft,
    scene: {
      ...draft.scene,
      meshGroups: groups,
      meshAssignments: groups.flatMap((group) => group.meshNames.map((meshName) => ({ meshName, groupId: group.id, kind: group.kind }))),
    },
    config: {
      ...draft.config,
      groups: configGroups,
      selectedGroupId: groups[0]?.id ?? draft.config.selectedGroupId,
    },
  };
}

export function normalizeProductRenderDraft(payload: ProductRenderPayload): ProductRenderDraft | null {
  if (!payload.record?.id || !payload.upload?.id || !payload.snapshot?.sceneJson) {
    return null;
  }

  const uploadId = payload.upload.id;
  const scene = normalizeSceneDocument(payload.snapshot.sceneJson, uploadId);
  const config = normalizeEditorConfig(payload.snapshot.configJson);
  for (const group of scene.meshGroups) {
    if (!config.groups[group.id]) {
      config.groups[group.id] = createDefaultAppearance(group.kind);
    }
  }

  return {
    recordId: payload.record.id,
    uploadId,
    fileName: payload.upload.originalFileName || "product-render.fbx",
    scene,
    config,
  };
}

export async function fetchProductRenderDraft(goodsId: string, legacyModelId?: string) {
  const query = new URLSearchParams();
  if (legacyModelId) {
    query.set("legacyModelId", legacyModelId);
  }

  const suffix = query.toString() ? `?${query.toString()}` : "";
  const response = await fetch(`/api/render/persistence/products/${encodeURIComponent(goodsId)}/viewer${suffix}`, { cache: "no-store" });
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    return null;
  }

  return normalizeProductRenderDraft((await response.json()) as ProductRenderPayload);
}

export async function fetchProductRenderResources(goodsId: string, legacyModelId?: string) {
  const query = new URLSearchParams();
  if (legacyModelId) {
    query.set("legacyModelId", legacyModelId);
  }

  const suffix = query.toString() ? `?${query.toString()}` : "";
  const response = await fetch(`/api/render/persistence/products/${encodeURIComponent(goodsId)}/resources${suffix}`, { cache: "no-store" });
  if (!response.ok) {
    return null;
  }

  const payload = (await response.json()) as ProductRenderResourcePayload;
  return Array.isArray(payload.mappings) ? payload : null;
}
